import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { withStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import localizationManager from "./localizationManager";
import Helmet from "react-helmet";

const styles = (theme) => ({
  root: {
    padding: theme.spacing.unit * 4,
    maxWidth: 1200,
    margin: "0 auto",
    marginTop: 80, // for AppBar
    minHeight: "70vh",
  },
  header: {
    marginBottom: theme.spacing.unit * 4,
    textAlign: "center",
  },
  articleCard: {
    padding: 0, // Remove padding from Paper, add it to content div
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden", // Ensure image borderRadius works
    transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[4],
    },
    textDecoration: "none",
    boxSizing: "border-box",
  },
  articleImage: {
    width: "100%",
    height: 180,
    objectFit: "cover",
    marginBottom: theme.spacing.unit * 2,
  },
  articleContent: {
    padding: `0 ${theme.spacing.unit * 3}px ${theme.spacing.unit * 3}px`,
  },
  gridItem: {
    display: "flex",
    flexDirection: "column",
    paddingBottom: `${theme.spacing.unit * 4}px !important`,
  },
  articleCategory: {
    color: theme.palette.secondary.main,
    fontWeight: 600,
    marginBottom: theme.spacing.unit,
    textTransform: "uppercase",
    fontSize: "0.85rem",
  },
  articleTitle: {
    marginBottom: theme.spacing.unit * 2,
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  articleExcerpt: {
    flexGrow: 1,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing.unit * 3,
  },
  readMore: {
    color: theme.palette.secondary.main,
    fontWeight: 600,
    marginTop: "auto",
  },
});

class Articles extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      articles: [],
      loading: true,
    };
  }

  componentDidMount() {
    this.fetchArticles();
  }

  fetchArticles = async () => {
    try {
      const response = await fetch("/data/articles.json");
      if (!response.ok) {
        throw new Error("Network response was not ok.");
      }
      const data = await response.json();
      this.setState({ articles: data, loading: false });
    } catch (error) {
      console.error("Error fetching articles:", error);
      this.setState({ loading: false });
    }
  };

  render() {
    const { classes } = this.props;
    const { articles, loading } = this.state;
    // Current lang handling
    let lang = localizationManager.currentLang;
    if (lang === "zh" || lang === "zh-tw") {
      lang = "zh-TW";
    }
    const langPrefix = lang !== "en" ? `/${lang}` : "";

    const pageTitle = localizationManager.getLocalStringPlain("@@knowledge_hub");

    return (
      <div className={classes.root}>
        <Helmet>
          <title>{pageTitle + " | " + localizationManager.getLocalStringPlain("@@linkchamp")}</title>
          <meta
            name="description"
            content={localizationManager.getLocalString("@@knowledge_hub_desc")}
          />
        </Helmet>

        <Typography variant="h3" className={classes.header}>
          {pageTitle}
        </Typography>

        {loading ? (
          <Typography variant="body1" align="center">
            {localizationManager.getLocalString("@@loading")}
          </Typography>
        ) : (
          <Grid container spacing={32}>
            {articles.map((article) => {
              const localeData = article.locales[lang] || article.locales["en"];
              return (
                <Grid item xs={12} sm={6} md={4} key={article.id} className={classes.gridItem}>
                  <Paper
                    className={classes.articleCard}
                    component={Link}
                    to={`${langPrefix}/articles/${article.slug}`}
                  >
                    <img
                      src={article.image}
                      className={classes.articleImage}
                      alt={localeData.title}
                    />
                    <div className={classes.articleContent}>
                      <Typography className={classes.articleCategory}>
                        {article.category}
                      </Typography>
                      <Typography variant="h6" className={classes.articleTitle}>
                        {localeData.title}
                      </Typography>
                      <Typography className={classes.articleExcerpt}>
                        {localeData.excerpt}
                      </Typography>
                      <Typography className={classes.readMore}>
                        {localizationManager.getLocalString("@@read_more")} →
                      </Typography>
                    </div>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}
      </div>
    );
  }
}

Articles.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(Articles);
